<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Header -->
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border-b px-6 py-4'
      ]"
    >
      <div class="flex items-center justify-between">
        <div>
          <h1
            :class="[theme.isDark.value ? 'text-gray-100' : 'text-gray-900', 'text-2xl font-bold']"
          >
            Firestore Index Usage
          </h1>
          <p :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'mt-1 text-sm']">
            Monitor composite index usage across databases and collections
          </p>
        </div>
        <router-link
          to="/database"
          :class="[
            theme.isDark.value
              ? 'text-gray-400 hover:text-gray-200'
              : 'text-gray-600 hover:text-gray-900',
            'text-sm font-medium'
          ]"
        >
          ← Back to Database
        </router-link>
      </div>
    </div>

    <div class="p-6 space-y-6">
      <!-- Selection Controls -->
      <div
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg overflow-hidden'
        ]"
      >
        <h2
          :class="[
            theme.isDark.value ? 'text-gray-100 border-gray-700' : 'text-gray-900 border-gray-200',
            'text-lg font-semibold px-6 py-4 border-b'
          ]"
        >
          Select Collection
        </h2>
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <!-- Environment Select -->
            <div>
              <label
                for="firestore-env-select"
                :class="[
                  theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
                  'block text-sm font-medium mb-2'
                ]"
              >
                Environment
              </label>
              <select
                id="firestore-env-select"
                v-model="firestoreEnv"
                :class="[
                  theme.isDark.value
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900',
                  'w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                ]"
                @change="onEnvChange"
              >
                <option value="staging">Staging</option>
                <option value="production">Production</option>
              </select>
            </div>

            <!-- Database Select -->
            <div>
              <label
                for="firestore-db-select"
                :class="[
                  theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
                  'block text-sm font-medium mb-2'
                ]"
              >
                Database
              </label>
              <select
                id="firestore-db-select"
                v-model="selectedDatabase"
                :class="[
                  theme.isDark.value
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900',
                  'w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50'
                ]"
              >
                <option value="">Select a database</option>
                <option
                  v-for="db in databases"
                  :key="db"
                  :value="db"
                >
                  {{ db }}
                </option>
              </select>
            </div>

            <!-- Collection Select -->
            <div>
              <label
                for="firestore-collection-select"
                :class="[
                  theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
                  'block text-sm font-medium mb-2'
                ]"
              >
                Collection (Kind)
              </label>
              <select
                id="firestore-collection-select"
                v-model="selectedKind"
                :disabled="kindsLoading || kinds.length === 0"
                :class="[
                  theme.isDark.value
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900',
                  'w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50'
                ]"
              >
                <option value="">
                  {{
                    kindsLoading
                      ? 'Loading...'
                      : selectedDatabase
                        ? 'Select a collection'
                        : 'Select database first'
                  }}
                </option>
                <option
                  v-for="kind in kinds"
                  :key="kind"
                  :value="kind"
                >
                  {{ kind }}
                </option>
              </select>
            </div>
          </div>

          <!-- Error Messages -->
          <p
            v-if="kindsError"
            class="mt-4 text-sm text-red-600 dark:text-red-400"
          >
            {{ kindsError }}
          </p>
        </div>
      </div>

      <!-- Index Usage Section -->
      <div
        v-if="selectedKind"
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg overflow-hidden'
        ]"
      >
        <div
          :class="[
            theme.isDark.value ? 'border-gray-700' : 'border-gray-200',
            'px-6 py-4 border-b flex flex-col md:flex-row md:items-center md:justify-between gap-4'
          ]"
        >
          <div>
            <h2
              :class="[
                theme.isDark.value ? 'text-gray-100' : 'text-gray-900',
                'text-lg font-semibold'
              ]"
            >
              Composite Index Usage
            </h2>
            <p :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'text-sm mt-1']">
              {{ selectedDatabase }} / {{ selectedKind }}
            </p>
          </div>

          <!-- Filters & Actions -->
          <div class="flex flex-wrap items-center gap-3">
            <!-- Filter unused -->
            <label
              :class="[
                theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
                'flex items-center gap-2 text-sm cursor-pointer'
              ]"
            >
              <input
                v-model="showOnlyUnused"
                type="checkbox"
                class="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              Unused only
            </label>

            <!-- Sort -->
            <select
              v-model="sortBy"
              :class="[
                theme.isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-100'
                  : 'bg-white border-gray-300 text-gray-900',
                'rounded-md border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
              ]"
            >
              <option value="lastUsed-desc">Last Used (Recent)</option>
              <option value="lastUsed-asc">Last Used (Oldest)</option>
              <option value="id-asc">ID (A-Z)</option>
              <option value="id-desc">ID (Z-A)</option>
            </select>

            <!-- Refresh Button -->
            <button
              :disabled="indexUsageLoading"
              :class="[
                theme.isDark.value
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white',
                'px-4 py-1.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              ]"
              @click="loadIndexUsage"
            >
              {{ indexUsageLoading ? 'Loading...' : 'Refresh' }}
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div
          v-if="indexUsageLoading"
          class="p-12 flex items-center justify-center"
        >
          <div class="flex items-center gap-3">
            <svg
              class="animate-spin h-5 w-5 text-blue-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span :class="theme.isDark.value ? 'text-gray-300' : 'text-gray-600'">
              Loading index usage data...
            </span>
          </div>
        </div>

        <!-- Error State -->
        <div
          v-else-if="indexUsageError"
          class="p-8 text-center"
        >
          <p class="text-red-600 dark:text-red-400">{{ indexUsageError }}</p>
          <button
            class="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
            @click="loadIndexUsage"
          >
            Retry
          </button>
        </div>

        <!-- Index Usage Table -->
        <div
          v-else-if="filteredIndexStats.length > 0"
          class="overflow-x-auto"
        >
          <!-- Summary Stats -->
          <div
            :class="[
              theme.isDark.value ? 'bg-gray-700/30 border-gray-700' : 'bg-gray-50 border-gray-200',
              'px-6 py-3 border-b grid grid-cols-2 md:grid-cols-4 gap-4'
            ]"
          >
            <div>
              <div
                :class="[
                  theme.isDark.value ? 'text-gray-400' : 'text-gray-500',
                  'text-xs uppercase tracking-wider'
                ]"
              >
                Total Indexes
              </div>
              <div
                :class="[
                  theme.isDark.value ? 'text-gray-100' : 'text-gray-900',
                  'text-xl font-semibold mt-1'
                ]"
              >
                {{ indexUsageData?.summary.total || 0 }}
              </div>
            </div>
            <div>
              <div
                :class="[
                  theme.isDark.value ? 'text-green-400' : 'text-green-600',
                  'text-xs uppercase tracking-wider'
                ]"
              >
                Used
              </div>
              <div
                :class="[
                  theme.isDark.value ? 'text-green-300' : 'text-green-700',
                  'text-xl font-semibold mt-1'
                ]"
              >
                {{ indexUsageData?.summary.used || 0 }}
              </div>
            </div>
            <div>
              <div
                :class="[
                  theme.isDark.value ? 'text-amber-400' : 'text-amber-600',
                  'text-xs uppercase tracking-wider'
                ]"
              >
                Unused
              </div>
              <div
                :class="[
                  (indexUsageData?.summary?.unused ?? 0) > 0
                    ? 'text-amber-500'
                    : theme.isDark.value
                      ? 'text-gray-100'
                      : 'text-gray-900',
                  'text-xl font-semibold mt-1'
                ]"
              >
                {{ indexUsageData?.summary.unused || 0 }}
              </div>
            </div>
            <div>
              <div
                :class="[
                  theme.isDark.value ? 'text-gray-400' : 'text-gray-500',
                  'text-xs uppercase tracking-wider'
                ]"
              >
                Showing
              </div>
              <div
                :class="[
                  theme.isDark.value ? 'text-gray-100' : 'text-gray-900',
                  'text-xl font-semibold mt-1'
                ]"
              >
                {{ filteredIndexStats.length }}
              </div>
            </div>
          </div>

          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead
              :class="[
                theme.isDark.value ? 'bg-gray-700/50' : 'bg-gray-50',
                'text-left text-xs font-medium uppercase tracking-wider'
              ]"
            >
              <tr>
                <th :class="[theme.isDark.value ? 'text-gray-300' : 'text-gray-600', 'px-6 py-3']">
                  Index ID
                </th>
                <th :class="[theme.isDark.value ? 'text-gray-300' : 'text-gray-600', 'px-6 py-3']">
                  Fields
                </th>
                <th :class="[theme.isDark.value ? 'text-gray-300' : 'text-gray-600', 'px-6 py-3']">
                  Query Scope
                </th>
                <th :class="[theme.isDark.value ? 'text-gray-300' : 'text-gray-600', 'px-6 py-3']">
                  Last Used
                </th>
                <th :class="[theme.isDark.value ? 'text-gray-300' : 'text-gray-600', 'px-6 py-3']">
                  Tracked Since
                </th>
                <th
                  :class="[
                    theme.isDark.value ? 'text-gray-300' : 'text-gray-600',
                    'px-6 py-3 text-center'
                  ]"
                >
                  Status
                </th>
              </tr>
            </thead>
            <tbody
              :class="[theme.isDark.value ? 'divide-gray-700' : 'divide-gray-200', 'divide-y']"
            >
              <tr
                v-for="idx in filteredIndexStats"
                :key="idx.indexId"
                :class="[
                  theme.isDark.value
                    ? 'bg-gray-800 hover:bg-gray-700/50'
                    : 'bg-white hover:bg-gray-50'
                ]"
              >
                <td
                  :class="[
                    theme.isDark.value ? 'text-gray-300' : 'text-gray-600',
                    'px-6 py-4 text-sm font-mono'
                  ]"
                >
                  {{ idx.alphanumericId }}
                </td>
                <td class="px-6 py-4">
                  <div class="flex flex-wrap gap-1.5">
                    <span
                      v-for="(prop, propIdx) in idx.properties"
                      :key="propIdx"
                      :class="[
                        theme.isDark.value
                          ? 'bg-gray-700 text-gray-200'
                          : 'bg-gray-100 text-gray-800',
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium'
                      ]"
                    >
                      <span
                        v-if="prop.direction === 'ASCENDING'"
                        class="text-green-500"
                        >↑</span
                      >
                      <span
                        v-else
                        class="text-amber-500"
                        >↓</span
                      >
                      {{ prop.name }}
                    </span>
                  </div>
                </td>
                <td
                  :class="[
                    theme.isDark.value ? 'text-gray-400' : 'text-gray-500',
                    'px-6 py-4 text-sm'
                  ]"
                >
                  {{ idx.ancestor === 'COLLECTION' ? 'Collection' : 'Collection Group' }}
                </td>
                <td
                  :class="[
                    idx.lastKnownUsageTime
                      ? theme.isDark.value
                        ? 'text-gray-300'
                        : 'text-gray-600'
                      : 'text-amber-500',
                    'px-6 py-4 text-sm'
                  ]"
                >
                  {{ idx.lastKnownUsageTime ? formatDate(idx.lastKnownUsageTime) : 'Never used' }}
                </td>
                <td
                  :class="[
                    theme.isDark.value ? 'text-gray-400' : 'text-gray-500',
                    'px-6 py-4 text-sm'
                  ]"
                >
                  {{ formatDate(idx.statTrackedSinceTime) }}
                </td>
                <td class="px-6 py-4 text-center">
                  <span
                    v-if="!idx.lastKnownUsageTime"
                    :class="[
                      theme.isDark.value
                        ? 'bg-amber-900/50 text-amber-300'
                        : 'bg-amber-100 text-amber-800',
                      'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium'
                    ]"
                  >
                    Unused
                  </span>
                  <span
                    v-else
                    :class="[
                      theme.isDark.value
                        ? 'bg-green-900/50 text-green-300'
                        : 'bg-green-100 text-green-800',
                      'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium'
                    ]"
                  >
                    Active
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="!indexUsageLoading && !indexUsageError"
          class="p-12 text-center"
        >
          <svg
            :class="[
              theme.isDark.value ? 'text-gray-600' : 'text-gray-400',
              'w-12 h-12 mx-auto mb-4'
            ]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'text-sm']">
            {{
              showOnlyUnused
                ? 'No unused indexes found'
                : 'No composite indexes found for this collection'
            }}
          </p>
        </div>
      </div>

      <!-- Prompt to select collection -->
      <div
        v-else
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-12 text-center'
        ]"
      >
        <svg
          :class="[
            theme.isDark.value ? 'text-gray-600' : 'text-gray-400',
            'w-16 h-16 mx-auto mb-4'
          ]"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
          />
        </svg>
        <h3
          :class="[
            theme.isDark.value ? 'text-gray-200' : 'text-gray-900',
            'text-lg font-medium mb-2'
          ]"
        >
          Select a Collection
        </h3>
        <p :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'text-sm']">
          Choose an environment, database, and collection above to view composite index usage.
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject, ref, watch, computed } from 'vue'
import { useTheme } from '../../utils/useTheme'
import { firestoreService } from '../../services/database/firestoreService'
import type {
  FirestoreEnv,
  FirestoreIndexUsageResponse
} from '../../services/database/firestoreService'

export default defineComponent({
  name: 'DatabaseFirestore',
  setup() {
    const theme = inject('theme', useTheme())

    // Selection state
    const firestoreEnv = ref<FirestoreEnv>('staging')
    const selectedDatabase = ref('')
    const selectedKind = ref('')

    // Databases (loaded from service based on env)
    const databases = ref<string[]>([])

    // Kinds (collections)
    const kinds = ref<string[]>([])
    const kindsLoading = ref(false)
    const kindsError = ref('')

    // Index Usage
    const indexUsageData = ref<FirestoreIndexUsageResponse | null>(null)
    const indexUsageLoading = ref(false)
    const indexUsageError = ref('')

    // UI State
    const showOnlyUnused = ref(false)
    const sortBy = ref('lastUsed-desc')

    // Computed: Filtered and sorted indexes
    const filteredIndexStats = computed(() => {
      if (!indexUsageData.value) return []

      let result = [...indexUsageData.value.indexStats]

      // Filter unused
      if (showOnlyUnused.value) {
        result = result.filter((idx) => !idx.lastKnownUsageTime)
      }

      // Sort
      const [field, direction] = sortBy.value.split('-')
      result.sort((a, b) => {
        let comparison = 0
        if (field === 'lastUsed') {
          // Handle null values - unused indexes go to end when sorting by recent
          const aTime = a.lastKnownUsageTime ? new Date(a.lastKnownUsageTime).getTime() : 0
          const bTime = b.lastKnownUsageTime ? new Date(b.lastKnownUsageTime).getTime() : 0
          comparison = aTime - bTime
        } else if (field === 'id') {
          comparison = a.alphanumericId.localeCompare(b.alphanumericId)
        }
        return direction === 'desc' ? -comparison : comparison
      })

      return result
    })

    // Load databases based on environment
    function loadDatabases() {
      databases.value = firestoreService.getDatabases(firestoreEnv.value)
    }

    // Load kinds (collections)
    async function loadKinds() {
      if (!selectedDatabase.value) {
        kinds.value = []
        return
      }
      kindsLoading.value = true
      kindsError.value = ''
      try {
        const res = await firestoreService.getKinds(selectedDatabase.value, firestoreEnv.value)
        kinds.value = res.kinds ?? []
      } catch (e) {
        kindsError.value = e instanceof Error ? e.message : 'Unable to load collections'
        kinds.value = []
      } finally {
        kindsLoading.value = false
      }
    }

    // Load index usage
    async function loadIndexUsage() {
      if (!selectedDatabase.value || !selectedKind.value) {
        indexUsageData.value = null
        return
      }
      indexUsageLoading.value = true
      indexUsageError.value = ''
      try {
        const res = await firestoreService.getIndexUsage(
          selectedDatabase.value,
          selectedKind.value,
          firestoreEnv.value
        )
        indexUsageData.value = res
      } catch (e) {
        indexUsageError.value = e instanceof Error ? e.message : 'Unable to load index usage'
        indexUsageData.value = null
      } finally {
        indexUsageLoading.value = false
      }
    }

    // Event handlers
    function onEnvChange() {
      selectedDatabase.value = ''
      selectedKind.value = ''
      kinds.value = []
      indexUsageData.value = null
      loadDatabases()
    }

    // Utility functions
    function formatDate(dateStr: string | null) {
      if (!dateStr) return 'Never'
      try {
        const date = new Date(dateStr)
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch {
        return dateStr
      }
    }

    // Watchers
    watch(selectedDatabase, () => {
      selectedKind.value = ''
      indexUsageData.value = null
      loadKinds()
    })

    watch(selectedKind, () => {
      indexUsageData.value = null
      if (selectedKind.value) {
        loadIndexUsage()
      }
    })

    // Initial load
    loadDatabases()

    return {
      theme,
      firestoreEnv,
      onEnvChange,
      selectedDatabase,
      selectedKind,
      databases,
      kinds,
      kindsLoading,
      kindsError,
      indexUsageData,
      indexUsageLoading,
      indexUsageError,
      loadIndexUsage,
      filteredIndexStats,
      showOnlyUnused,
      sortBy,
      formatDate
    }
  }
})
</script>
